import { observer } from "mobx-react";
import { useLocalStore } from "mobx-react-lite";
import React from "react";
import { useIntersectionObserver } from "../utils/useIntersectionObserver";
import { useRef, useContext } from "react";
import { analytics } from "../../src";

export enum AnalyticsEvents {
  PictureEnterBottom = "picture-enter-bottom",
  PictureLeaveBottom = "picture-leave-bottom",
  PictureEnterTop = "picture-enter-top",
  PictureLeaveTop = "picture-leave-top",
  PictureCenter = "picture-center",
  ChangeLanguage = "change-language",
  ChangeStartLocation = "change-start-location",
  ChangeDestinationLocation = "change-destination-location",
  ToggleWheelchair = "toggle-wheelchair",
  SwapStartDestination = "swap-start-destination",
  ClickWasInstructionHelpful = "click-was-instruction-helpful",
  PressShareEmail = "press-share-email",
  SubmitShareEmail = "submit-share-email",
  PressShareLink = "press-share-link",
  ToggleUseShortLink = "toggle-use-short-link",
  ToggleBlockStartingPosition = "toggle-block-starting-position",
  PressCopyLink = "press-copy-link",
  TouchScroll = "touch-scroll",
  BecomesIdle = "becomes-idle",
  BecomesVisible = "becomes-visible",
  SessionStart = "session-start",
  RouteChange = "route-change",
}

export type ScrollDataType = {
  scrolling: boolean;
  time: number;
  directionX: "left" | "right" | null;
  directionY: "up" | "down" | null;
  speedX: number;
  speedY: number;
  totalDistanceX: number;
  totalDistanceY: number;
  relativeDistanceX: number;
  relativeDistanceY: number;
  positionX: number;
  positionY: number;
};

export type OptionsType = {
  onScrollStart?: Function;
  onScrollEnd?: Function;
};

const SCROLL_END_DURATION = 100;

const INITIAL_DATA = {
  scrolling: false,
  time: 0,

  directionX: null,
  directionY: null,
  speedX: 0,
  speedY: 0,
  totalDistanceX: 0,
  totalDistanceY: 0,
  relativeDistanceX: 0,
  relativeDistanceY: 0,
  positionX: 0,
  positionY: 0
};

function getPositionX() {
  return window.pageXOffset || 0;
}

function getPositionY() {
  return window.pageYOffset || 0;
}

function getDirectionX(x: number, frameValues: ScrollDataType): "left" | "right" | null {
  if (x > frameValues.positionX) return "right";
  if (x < frameValues.positionX) return "left";
  return null;
}

function getDirectionY(y: number, frameValues: ScrollDataType): "up" | "down" | null {
  if (y > frameValues.positionY) return "down";
  if (y < frameValues.positionY) return "up";
  return null;
}

function getTotalDistanceX(x: number, frameValues: ScrollDataType): number {
  return frameValues.totalDistanceX + Math.abs(x - frameValues.positionX);
}

function getTotalDistanceY(y: number, frameValues: ScrollDataType): number {
  return frameValues.totalDistanceY + Math.abs(y - frameValues.positionY);
}

function getRelativeDistanceX(x: number, startValues: ScrollDataType): number {
  return Math.abs(x - startValues.positionX);
}

function getRelativeDistanceY(y: number, startValues: ScrollDataType): number {
  return Math.abs(y - startValues.positionY);
}

export const useScrollData = (options: OptionsType = {}): ScrollDataType => {
  const [data, setData] = React.useState<ScrollDataType>(INITIAL_DATA);
  const startValues = React.useRef<ScrollDataType>(INITIAL_DATA);
  const frameValues = React.useRef<ScrollDataType>(INITIAL_DATA);
  const startTimestamp = React.useRef<number | null>();
  const frameTimestamp = React.useRef<number | null>();
  const scrollTimeout = React.useRef<any>(null);
  const raf = React.useRef<any>(null);

  function frame(timestamp: number) {
    if (!startTimestamp.current) startTimestamp.current = timestamp;

    // Calculate the time in ms that scrolling is active
    const time = timestamp - startTimestamp.current;

    // Set new position values
    const positionX = getPositionX();
    const positionY = getPositionY();

    // Set new direction values
    const directionX = getDirectionX(positionX, frameValues.current);
    const directionY = getDirectionY(positionY, frameValues.current);

    // Set new totalDistance values
    const totalDistanceX = getTotalDistanceX(positionX, frameValues.current);
    const totalDistancey = getTotalDistanceY(positionY, frameValues.current);

    // Set new relativeDistance values
    const relativeDistanceX = getRelativeDistanceX(positionX, startValues.current);
    const relativeDistanceY = getRelativeDistanceY(positionY, startValues.current);

    // Set new speed values
    const timestampDiff = timestamp - (frameTimestamp.current || 0);
    const speedX = ((frameValues.current.positionX - positionX) / Math.max(1, timestampDiff)) * 1000;
    const speedY = ((frameValues.current.positionY - positionY) / Math.max(1, timestampDiff)) * 1000;

    const nextframeValues = {
      ...frameValues.current,
      scrolling: true,
      time,
      positionX,
      positionY,
      directionX,
      directionY,
      totalDistanceX,
      totalDistancey,
      relativeDistanceX,
      relativeDistanceY,
      speedX,
      speedY
    };

    // Store new values
    frameValues.current = nextframeValues;

    // Update the state
    setData(nextframeValues);

    // Set frameTimestamp for speed calculation
    frameTimestamp.current = timestamp;

    // We're still scrolling, so call tick method again
    raf.current = requestAnimationFrame(frame);
  }

  function clearAndSetscrollTimeout() {
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(scrollEnd, SCROLL_END_DURATION);
  }

  function onScroll() {
    if (!frameValues.current.scrolling) {
      scrollStart();
    }

    clearAndSetscrollTimeout();
  }

  function scrollStart() {
    // Save data at the moment of starting so we have
    // something to compare the current values against
    startValues.current = { ...frameValues.current };

    // Start RAF
    raf.current = requestAnimationFrame(frame);

    // If present, call onScrollStart function
    if (typeof options.onScrollStart === "function") {
      options.onScrollStart();
    }
  }

  function scrollEnd() {
    // Reset scroll data
    frameValues.current = {
      ...frameValues.current,
      scrolling: false,
      time: 0,
      directionX: null,
      directionY: null,
      speedX: 0,
      speedY: 0,
      totalDistanceX: 0,
      totalDistanceY: 0,
      relativeDistanceX: 0,
      relativeDistanceY: 0,
      positionX: 0,
      positionY: 0
    };

    // Update the state
    setData(frameValues.current);

    // Cancel RAF
    cancelAnimationFrame(raf.current);
    startTimestamp.current = null;
    frameTimestamp.current = null;

    // If present, call onScrollEnd function
    if (typeof options.onScrollEnd === "function") {
      options.onScrollEnd();
    }
  }

  React.useEffect(() => {
    // Add scrollListener
    window.addEventListener("scroll", onScroll, { passive: true });

    // Remove listener when unmounting
    return () => {
      clearTimeout(scrollTimeout.current);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  // Return data with rounded values
  return {
    ...data,
    time: Math.round(data.time),
    speedX: Math.round(data.speedX),
    speedY: Math.round(data.speedY),
    totalDistanceX: Math.round(data.totalDistanceX),
    totalDistanceY: Math.round(data.totalDistanceY),
    relativeDistanceX: Math.round(data.relativeDistanceX),
    relativeDistanceY: Math.round(data.relativeDistanceY),
    positionX: Math.round(data.positionX),
    positionY: Math.round(data.positionY)
  };
};

export const ScrollSpeedContext = React.createContext({
  scrollData: INITIAL_DATA as ScrollDataType
});
export const ScrollSpeedObserver = observer((props: { children: any }) => {
  const scrollData = useScrollData();

  return <ScrollSpeedContext.Provider value={{ scrollData }}>{props.children}</ScrollSpeedContext.Provider>;
});

export const cachedIntersectionObserver = (
  entry: IntersectionObserverEntry,
  onChange: (isIntersecting: boolean) => void
) => {
  const cache = useRef({
    wasVisible: false,
    lastValue: undefined as undefined | boolean
  });

  if (entry?.isIntersecting && cache.current.lastValue !== true) {
    onChange(entry?.isIntersecting);
    cache.current.lastValue = true;
    cache.current.wasVisible = true;
  } else if (entry?.isIntersecting === false && cache.current.wasVisible && cache.current.lastValue !== false) {
    onChange(entry?.isIntersecting);
    cache.current.lastValue = false;
  }
};

const analyticsCall: typeof analytics.logEvent = (...args) => {
  console.log(...args);
  // @ts-ignore
  analytics.logEvent(...args);
};

export const usePictureAnalyticsObservers = (current: any, _logData: any) => {
  const { scrollData } = useContext(ScrollSpeedContext);

  const logData = { ..._logData, ...scrollData };

  const t1 = useIntersectionObserver(current, { threshold: 0.5 });
  cachedIntersectionObserver(t1!, isIntersecting => {
    if (isIntersecting) {
      if (scrollData.directionY === "up") {
        analyticsCall(AnalyticsEvents.PictureEnterTop, logData);
      } else {
        analyticsCall(AnalyticsEvents.PictureEnterBottom, logData);
      }
    } else {
      if (scrollData.directionY === "up") {
        analyticsCall(AnalyticsEvents.PictureLeaveBottom, logData);
      } else {
        analyticsCall(AnalyticsEvents.PictureLeaveTop, logData);
      }
    }
  });

  const tCenter = useIntersectionObserver(current, { threshold: 0.5, rootMargin: "0px 0px -50% 0px" });
  cachedIntersectionObserver(tCenter!, isIntersecting => {
    if (tCenter!.intersectionRect.y > 0) {
      analyticsCall(AnalyticsEvents.PictureCenter, logData);
    }
  });
};
